import { useState } from 'react';
import {
	add,
	ChargeStateXref,
	ChargeStateXrefSchema,
} from '@/api/fee/charge-state-xref';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { states } from '@/constants/states';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CirclePlus } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function AddChargeStateXref({ chargeId }: { chargeId: number }) {
	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: (newChargeStateXref: ChargeStateXref) => add(newChargeStateXref),
		onSuccess: () => {
			toast.success(`Success! State mapping has been successfully added.`, {
				dismissible: true,
			});
			setOpen(false);
			queryClient.invalidateQueries({
				queryKey: ['state-mappings'],
			});
			reset();
		},
		onError: () => {
			toast.error(`Error! State mapping could not be added.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<ChargeStateXref>({
		resolver: zodResolver(ChargeStateXrefSchema),
		mode: 'all',
		defaultValues: {
			chargeId: chargeId,
		},
	});

	const { reset } = form;
	const onSubmit: SubmitHandler<ChargeStateXref> = (values: ChargeStateXref) =>
		mutate(values);

	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" onClick={() => setOpen(true)}>
							<CirclePlus />
							Add New State Mapping
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Add a new state mapping</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent>
					<SheetHeader>
						<SheetTitle>Add a New State Mapping</SheetTitle>
						<SheetDescription></SheetDescription>
					</SheetHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="space-y-4">
								<FormField
									control={form.control}
									name="feeDescId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>FeeDescID</FormLabel>
											<FormControl>
												<Input
													{...field}
													value={field.value ?? ''}
													type="number"
													onChange={(e) => {
														const val = e.target.value;
														field.onChange(val === '' ? undefined : Number(val));
													}}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="state"
									render={({ field }) => (
										<FormItem>
											<FormLabel>State</FormLabel>
											<FormControl>
												<Select
													value={field.value ?? 'none'}
													onValueChange={(val) =>
														field.onChange(val === 'none' ? null : val)
													}
												>
													<SelectTrigger>
														<SelectValue placeholder="Select a state..." />
													</SelectTrigger>
													<SelectContent>
														<SelectGroup>
															<SelectLabel>States</SelectLabel>
															{states!.map((state) => (
																<SelectItem key={state} value={state}>
																	{state}
																</SelectItem>
															))}
														</SelectGroup>
													</SelectContent>
												</Select>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="mt-9 flex justify-end">
								<Button
									type="submit"
									disabled={!form.formState.isValid || form.formState.isSubmitting}
								>
									{form.formState.isSubmitting ? 'Adding...' : 'Add'}
								</Button>
							</div>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</div>
	);
}
